import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmarkCircle, faPlus, faTrash, faFloppyDisk, faPenToSquare, faXmark, faCheck, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { useTheme } from '../../../../context/ThemeProvider'
import useAxiosPrivateSAI from '../../../../hooks/useAxiosPrivateSAI'
import {toast} from 'sonner'
import useAuth from '../../../../hooks/useAuth'

const ViewContactsSlideOut = ({ open, setOpen, policyData }) => {
  const theme = useTheme()
  const {auth} = useAuth()
  const axios = useAxiosPrivateSAI()
  const [contacts, setContacts] = useState([])
  const [editingIndex, setEditingIndex] = useState(null)
  const [editingName, setEditingName] = useState('')
  const [editingEmail, setEditingEmail] = useState('')
  const [isAdding, setIsAdding] = useState(false)
  const [newContact, setNewContact] = useState({ name: '', email: '', position: '' })
  const [schoolData, setSchoolData] = useState([])
  const [selectedSchools, setSelectedSchools] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const style = {
    backgroundColor: theme.backgroundColor,
    color: theme.textColor,
  }

  const handleClose = () => {
    setOpen(false)
    setIsAdding(false)
    setEditingIndex(null)
    setNewContact({ name: '', email: '', position: '' })
  }

  useEffect(() => {
    if (open) fetchContacts()
  }, [open, axios])

  const fetchContacts = async () => {
    try {
      setIsLoading(true)
      const orgId = JSON.parse(localStorage.getItem('orgType'))?.id
      if (!orgId) return

      const res = await axios.get(`/api/policy/contacts?current_id=${orgId}`)
      setContacts(res.data.contacts)
      setSchoolData(res.data.schoolData?.schools || [])
    } catch (err) {
      toast.error('Failed to fetch contacts:', err)
    }
    setIsLoading(false)
  }

  const handleAddContact = async () => {
    const { name, email, position } = newContact
  
    if (!name || !email || !position) {
      toast.error('Please fill out all fields.')
      return
    }
  
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address.')
      return
    }
  
    const payload = {
      contact_id: 0,
      contact_name: name,
      contact_email: email,
      contact_position: position,
      school_ids: selectedSchools,
      type: 'academy',
      cid: JSON.parse(localStorage.getItem('orgType'))?.id,
      roles: [1, 2, 3, 4],
    }
  
    try {
      const response = await axios.post('/api/policy/contacts/new', payload)
      if (response.status === 200 || response.data?.success) {
        toast.success('Contact added successfully.')
        fetchContacts()
        isAdding(false)
      } else {
        toast.error('Failed to add contact. Please try again.')
      }
    } catch (error) {
      console.error('Error adding contact:', error)
      toast.error('Failed to add contact.')
    }
  }
  

  const handleUpdateContact = async (index) => {
    const contact = contacts[index]
    try {
      await axios.post('/api/policy/contacts/update', {
        contact_id: contact.contact_id,
        contact_name: editingName,
        contact_email: editingEmail,
        type: 'academy',
        cid: JSON.parse(localStorage.getItem('orgType'))?.id,
      })
      toast.success('Contact updated successfully.')
      fetchContacts()
    } catch (error) {
      toast.error('Update failed:', error)
    }
  }

  const handleDeleteContact = async (index) => {
    const contact = contacts[index]
    try {
      await axios.post('/api/policy/contacts/delete', {
        contact_id: contact.contact_id,
        type: 'academy',
        cid: JSON.parse(localStorage.getItem('orgType'))?.id,
      })
      toast.success('Contact deleted.')
      fetchContacts()
    } catch (error) {
      toast.error('Delete failed:', error)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} className="relative z-[100]">
      <DialogBackdrop className="fixed inset-0 bg-gray-500 bg-opacity-75" />
      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <DialogPanel className="pointer-events-auto w-screen max-w-2xl transform transition-all">
              <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                <div className="px-4 py-6 sm:px-6" style={{ backgroundColor: style.backgroundColor }}>
                  <div className="flex items-center justify-between">
                    <DialogTitle className="text-lg font-semibold leading-6 text-white">
                      View your { policyData?.department === "Mutual" || policyData?.department === "GBS" ? 'Scheme' : 'Policy' } Contacts
                    </DialogTitle>
                    <button
                      type="button"
                      className="text-white"
                      onClick={handleClose}
                    >
                      <FontAwesomeIcon icon={faXmarkCircle} className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  <div className="flex items-center justify-end">
                    {!isAdding && (
                      <button
                        type="button"
                        onClick={() => setIsAdding(true)}
                        className="inline-flex items-center border bg-white border-sas-dark text-sas-dark rounded-md overflow-hidden text-sm font-medium">
                          <span className="px-4 py-2">Add Contact</span>
                          <span className="bg-sas-dark text-white px-3 py-2">
                            <FontAwesomeIcon icon={faPlus} />
                          </span>
                      </button>
                    )}
                  </div>

                  {isAdding && (
                    <div className="space-y-3">
                      <input
                        className="w-full border px-3 py-2 rounded"
                        placeholder="Name"
                        value={newContact.name}
                        onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                      />
                      <input
                        className="w-full border px-3 py-2 rounded"
                        placeholder="Email"
                        value={newContact.email}
                        onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                      />
                      <input
                        className="w-full border px-3 py-2 rounded"
                        placeholder="Position"
                        value={newContact.position}
                        onChange={(e) => setNewContact({ ...newContact, position: e.target.value })}
                      />
                      <div className="max-h-32 overflow-y-auto border rounded p-2">
                        {schoolData.map((school, idx) => (
                          <label key={idx} className="block text-sm">
                            <input
                              type="checkbox"
                              value={school.id}
                              checked={selectedSchools.includes(school.id)}
                              onChange={(e) => {
                                const checked = e.target.checked
                                const id = school.id
                                setSelectedSchools((prev) =>
                                  checked ? [...prev, id] : prev.filter((s) => s !== id)
                                )
                              }}
                              className="mr-2"
                            />
                            {school.name}
                          </label>
                        ))}
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={handleAddContact}
                          className="inline-flex items-center border bg-white border-sas-dark text-sas-dark rounded-md overflow-hidden text-sm font-medium">
                            <span className="px-4 py-2">Save</span>
                            <span className="bg-sas-dark text-white px-3 py-2">
                              <FontAwesomeIcon icon={faCheck} />
                            </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setIsAdding(false)}
                          className="inline-flex items-center border bg-white border-red-700 text-red-700 rounded-md overflow-hidden text-sm font-medium">
                            <span className="px-4 py-2">Cancel</span>
                            <span className="bg-red-700 text-white px-3 py-2">
                              <FontAwesomeIcon icon={faXmark} />
                            </span>
                        </button>
                      </div>
                    </div>
                  )}
                  <div>
                    {isLoading ? (
                      <div className="flex justify-center items-center h-full text-gray-500">
                        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                      </div>
                    ) : (
                      <>
                        {!isAdding && contacts.length > 0 && (
                          <div className="space-y-4">
                            <div className="border border-sas rounded-lg bg-gray-50 p-4">
                              <div>
                                <p className="font-semibold text-sas mb-1">{auth.user.contact_name} (You)</p>
                                <p className="text-sm text-gray-600">{auth.user.contact_email}</p>
                              </div>
                            </div>
                            {contacts.map((contact, idx) => (
                              <div
                                key={idx}
                                className={`border p-4 rounded-lg shadow-sm ${
                                  contact.contact_status === 'disabled' ? 'bg-orange-100 border-orange-300' : 'bg-gray-50'
                                }`}
                              >
                                {editingIndex === idx ? (
                                  <div>
                                    <div className='flex justify-end text-sm'> <FontAwesomeIcon
                                        icon={faXmarkCircle}
                                        className="text-red-600 cursor-pointer"
                                        onClick={() => setEditingIndex(null)}
                                      /></div>
                                    <label className='text-xs' htmlFor="name">Name</label>
                                    <input
                                      value={editingName}
                                      onChange={(e) => setEditingName(e.target.value)}
                                      className="w-full mb-2 border px-3 py-1 rounded"
                                    />
                                    <label className='text-xs' htmlFor="name">Email</label>
                                    <input
                                      value={editingEmail}
                                      onChange={(e) => setEditingEmail(e.target.value)}
                                      className="w-full mb-2 border px-3 py-1 rounded"
                                    />
                                    <div className="flex justify-end gap-3">
                                      <FontAwesomeIcon
                                        icon={faFloppyDisk}
                                        className="text-green-600 cursor-pointer"
                                        onClick={() => handleUpdateContact(idx)}
                                      />
                                      <FontAwesomeIcon
                                        icon={faTrash}
                                        className="text-red-500 cursor-pointer"
                                        onClick={() => handleDeleteContact(idx)}
                                      />
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex justify-between items-center">
                                    <div>
                                      <p className="font-semibold text-sas mb-1">{contact.contact_name}</p>
                                      <p className="text-sm text-gray-600">{contact.contact_email}</p>
                                      {contact.contact_status === 'disabled' && (
                                        <p className="text-xs text-orange-600 font-semibold mt-1">Pending - Update</p>
                                      )}
                                    </div>
                                    <FontAwesomeIcon
                                      icon={faPenToSquare}
                                      className={`${
                                        contact.contact_status === 'disabled' ? 'text-gray-400 cursor-not-allowed' : 'text-sas cursor-pointer'
                                      }`}
                                      onClick={() => {
                                        if (contact.contact_status !== 'disabled') {
                                          setEditingIndex(idx)
                                          setEditingName(contact.contact_name)
                                          setEditingEmail(contact.contact_email)
                                        }
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  )
}

export default ViewContactsSlideOut

